/**
 * Matchmaking overlay — shown when searching for an opponent.
 */

import { Loader2, XCircle } from "lucide-react";

interface MatchmakingOverlayProps {
  isOpen: boolean;
  gameType: string;
  searchTime: number; // milliseconds since search started
  onCancel: () => void;
}

export function MatchmakingOverlay({ isOpen, gameType, searchTime, onCancel }: MatchmakingOverlayProps) {
  if (!isOpen) return null;

  const seconds = Math.floor(searchTime / 1000);
  const minutes = Math.floor(seconds / 60);
  const displayTime = minutes > 0
    ? `${minutes}m ${seconds % 60}s`
    : `${seconds}s`;

  const gameNames: Record<string, string> = {
    tictactoe: "Tic-Tac-Toe",
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm">
      <div className="mx-4 w-full max-w-md rounded-2xl border border-border bg-card p-8 text-center shadow-2xl">
        {/* Spinner */}
        <div className="mb-6 flex justify-center">
          <div className="relative">
            <Loader2 className="h-16 w-16 animate-spin text-primary" />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="h-8 w-8 rounded-full bg-primary/20" />
            </div>
          </div>
        </div>

        <h2 className="mb-2 text-xl font-bold text-foreground">Finding Opponent</h2>
        <p className="mb-1 text-muted-foreground">
          Searching for a {gameNames[gameType] ?? gameType} match
        </p>
        <p className="mb-6 text-sm text-muted-foreground">
          Time elapsed: <span className="font-mono text-foreground">{displayTime}</span>
        </p>

        {/* Animated dots */}
        <div className="mb-8 flex items-center justify-center gap-2">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="h-2.5 w-2.5 animate-bounce rounded-full bg-primary"
              style={{ animationDelay: `${i * 150}ms` }}
            />
          ))}
        </div>

        <button
          onClick={onCancel}
          className="inline-flex items-center gap-2 rounded-lg border border-border px-5 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
        >
          <XCircle className="h-4 w-4" />
          Cancel
        </button>
      </div>
    </div>
  );
}